import { is } from '@common/is/is'
import { Number } from '@common/Number/Number'
import { JSON } from '@common/JSON/JSON'
import { DateTime } from '@common/DateTime/DateTime'

import Storage from '@common/Storage/Storage'

import type { IQueryParams } from '@common/Query/definitions'
import { Query } from '@common/Query/Query'

import { EClientRoute } from '@activator/codeService/definitions'

import globals from './globals'
import type { IAjaxParams } from './ajax'
import ajax from './ajax'

import crypter from './crypter'

export const DEMO_DAYS = 3

const TIMEOUT = 5000

const MIN_DAYS    = 0
const MIN_TIME_MS = 0

const enum EValidation {
  Invalid = 0,
  Valid   = -1
}

type TDeactivateCallback = (bool: boolean) => void

type TActivateCallback = (res?: number | string) => void

interface IOptions {
  readonly demoPeriodInMs:    number
  readonly checkIntervalInMs: number
  readonly server:            string
}

const DEFAULT_OPTIONS: IOptions = {
  demoPeriodInMs:    DEMO_DAYS * DateTime.DAY_IN_MS,
  checkIntervalInMs: DateTime.MINUTE_IN_MS,
  server:            globals.server
}

interface IData {
  readonly serial: string | null
  readonly code: string | null
  readonly registredAtInMs: number | null
  // Ezt a kettőt felül tudjuk csapni.
  timeLeftInMs: number
  lastCheckedInMs: number | null
}

const DEFAULT_DATA: IData = {
  timeLeftInMs:    DEFAULT_OPTIONS.demoPeriodInMs,
  serial:          null,
  code:            null,
  registredAtInMs: null,
  lastCheckedInMs: null
}

const STORAGE_ACTIVATOR_KEY = 'activator'

export enum EResponse {
  Invalid      = 0,
  AlreadyInUse = 1,
  ServerError  = 2
}

class Activator {
  private readonly options: IOptions = { ...DEFAULT_OPTIONS }
  // eslint-disable-next-line no-restricted-syntax -- Legacy.
  private data!: IData
  private storage?: Storage
  private checkTickIntervalId?: number

  /**
   * Namespace alapján inicializálás.
   * @param ns - A namespace, ami alapján Store-t példányosítunk.
   */
  public init (ns: string): void {
    this.storage = new Storage(ns)

    this.loadActivatorData()
    this.startCountingDown()
  }

  /** Hány van van még a demóból vagy az aktiválókód időtartamából. */
  public getDaysLeft (): number {
    const leftInDays = Math.ceil(this.data.timeLeftInMs / DateTime.DAY_IN_MS)

    return Math.max(MIN_DAYS, leftInDays)
  }

  /** Mikor fog lejárni az aktiváció? */
  public getExpiresAt (): Date | number {
    const { timeLeftInMs } = this.data

    if (is.infinite(timeLeftInMs)) {
      // eslint-disable-next-line no-restricted-globals
      return Infinity
    }

    const expiresAt = DateTime.now() + (timeLeftInMs as number)

    return new Date(expiresAt + DateTime.getTimezoneOffsetDiffInMs())
  }

  /** A szériaszámot tudjuk elkérni. */
  public getSerial (): string | null {
    return this.data.serial
  }

  /** Az aktivált aktiváló kód elkérése. */
  public getCode (): string | null {
    return this.data.code
  }

  /** Demo verzió fut éppen? */
  public isDemo (): boolean {
    return this.isRegistred() && !this.isActivated()
  }

  /** Vagy a demó, vagy a kód lejárt? */
  public isExpired (): boolean {
    return (this.data.timeLeftInMs <= MIN_TIME_MS)
  }

  /** Elkezdte a demót, vagy aktiálva lett kóddal az app? */
  public isRegistred (): boolean {
    return !is.null(this.data.registredAtInMs)
  }

  /** Aktiválva van? */
  public isActivated (): boolean {
    return !is.null(this.data.code)
  }

  /** Demóverzió elindítása. */
  public startDemo (): void {
    if (this.isRegistred()) {
      return
    }

    const now = DateTime.now()

    this.data = {
      ...this.data,
      registredAtInMs: now,
      lastCheckedInMs: now
    }

    this.saveActivatorData()
  }

  /**
   * App aktiválása.
   * Csak egyetlen egyszer lehet aktiválni.
   * @param code - A kód, amivel aktiválni próbálunk.
   * @param fn   - Aktiválás válaszát ebbe a callback-be dobjuk.
   * @throws Ha már aktiválva van, hibát dobunk.
   */
  public activate (code: string, fn: TActivateCallback): Promise<void> {
    if (this.isActivated()) {
      throw new Error('[ACTIVATOR] Ez a kód már aktiválva van.')
    }

    const url = `${ this.options.server }${ EClientRoute.Activate }`

    const ajaxOptions: IAjaxParams = {
      method: 'POST',
      data: { serial: globals.serial, code },
      timeout: TIMEOUT
    }

    return new Promise((resolve) => {
      // eslint-disable-next-line no-restricted-syntax -- A .then így maradjon.
      ajax(url, ajaxOptions)
        .then((res) => {
          // Itt nem kell csekkolni, mert ha a res nem jó, a catch ágba megyünk.
          const data = Query.decode(res)

          if (!is.number(data.valid_for)) {
            fn(EResponse.ServerError)
          }
          // Lehet már aktiválva van?
          else if (data.valid_for === EValidation.Invalid) {
            fn(Number.parse(data.reason, -1))
          }
          // Sikeres aktiválás
          else {
            const now = DateTime.now()

            const timeLeftInMs = data.valid_for === EValidation.Valid
              // eslint-disable-next-line no-restricted-globals
              ? Infinity
              : data.valid_for * DateTime.SECOND_IN_MS

            this.data = {
              ...this.data,
              timeLeftInMs,
              serial: globals.serial,
              code,
              registredAtInMs: now,
              lastCheckedInMs: now
            }

            this.saveActivatorData()

            fn()
          }

          resolve()
        })
        .catch(() => {
          // Ha nem jó ajax választ kaptunk, vagy bármilyen syntax errorunk van, akkor is fogjuk a szerverre :)
          fn(EResponse.ServerError)
          resolve()
        })
    })
  }

  /**
   * Deaktiválás.
   * Akár többször is el lehet sütni, nem lesz hiba.
   * @param fn - A szerver válasza után ezt a callback-et hívjuk meg.
   */
  public deactivate (fn: TDeactivateCallback): Promise<void> {
    return new Promise((resolve) => {
      if (is.null(this.data.code)) {
        fn(false)
        resolve()
      }
      else {
        const url = `${ this.options.server }${ EClientRoute.Deactivate }`

        const ajaxOptions: IAjaxParams = {
          method:  'POST',
          data:    { serial: this.data.serial ?? '', code: this.data.code },
          timeout: TIMEOUT
        }

        // eslint-disable-next-line no-restricted-syntax -- A .then így maradjon.
        ajax(url, ajaxOptions)
          .then((res) => {
            const data = Query.decode(res)

            if (!data.success) {
              fn(false)

              return
            }

            this.data = {
              ...this.data,
              registredAtInMs: null,
              lastCheckedInMs: null,
              serial:          null,
              code:            null,
              timeLeftInMs:    MIN_TIME_MS
            }

            this.saveActivatorData()

            fn(true)
            resolve()
          })
          .catch(() => {
            fn(false)
            resolve()
          })
      }
    })
  }

  /** Az adott app-nak van-e frissebb verziója? */
  public checkUpdate (): Promise<IQueryParams> {
    return new Promise((resolve, reject) => {
      const code = this.getCode()

      // eslint-disable-next-line no-restricted-syntax -- A .then így maradjon.
      ajax(`${ this.options.server }${ EClientRoute.Update }?serial=${ globals.serial }&code=${ code }`)
        .then((data) => {
          const updateData = Query.decode(data)

          resolve(updateData)
        })
        .catch(reject)
    })
  }

  /** Storage-ből az aktivációs adatok betöltése. */
  private loadActivatorData (): void {
    if (this.storage?.has(STORAGE_ACTIVATOR_KEY)) {
      // A store2 (régi 3rd party localStorage) valami escapelést rakott a szövegbe és legacy okokból ezt replaceljük
      const value = (this.storage.get(STORAGE_ACTIVATOR_KEY) ?? '')

      // Decrypt-el visszafordítjuk értelmes jsonná, majd parse
      const data = JSON.parse(crypter.decrypt(value))

      // Simán előfordulhat, hogy valami hiba történt, akkor default
      this.data = is.object(data) ? (data as unknown) as IData : { ...DEFAULT_DATA }
    }
    else {
      this.data = { ...DEFAULT_DATA }
    }
  }

  /** Aktuális aktivációs adatok elmentése localStorage-be. */
  private saveActivatorData (): void {
    const data = crypter.encrypt(JSON.stringify(this.data))
    this.storage?.set(STORAGE_ACTIVATOR_KEY, data)
  }

  /** Folyamatosan visszaszámolunk és mentjük az adatot. */
  private readonly check = (): void => {
    if (is.null(this.data.registredAtInMs)) {
      return
    }

    if (this.isExpired()) {
      window.clearInterval(this.checkTickIntervalId)

      return
    }

    const now           = DateTime.now()
    const diffInMs      = (now - (this.data.lastCheckedInMs ?? MIN_TIME_MS))
    const thresholdInMs = 200

    if (diffInMs > thresholdInMs) {
      this.data.timeLeftInMs = this.data.timeLeftInMs - diffInMs
    }

    this.data.lastCheckedInMs = now
    this.saveActivatorData()
  }

  /** Aktiváció után visszaszámolás. */
  private startCountingDown (): void {
    this.checkTickIntervalId = window.setInterval(this.check, this.options.checkIntervalInMs)
  }
}

const activator = new Activator()

export default activator
